import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Checkbox from '@narvar/nth-checkbox';
import TextInput from '@narvar/nth-text-input';
import {
  receiveUserPrefEmailAddress,
  receiveUserPreferencesCheckboxToggle,
} from '../../actions/userPreferences';
import { EMAIL_MISMATCH, RETURN_METHOD_PREFERENCES_LIST } from '../../constants/preferences';
import { titan } from '../../lib/productAnalytics';
import { PROMOTION_IDS, TITAN_EVENTS } from '../../constants/titan';

const Container = styled.div`
  margin: 40px auto 24px auto;
  max-width: 640px;
  label {
    font-weight: 400;
  }
`;

const EmailInputContainer = styled.div`
  margin: 1rem auto;
  text-align: center;
  label {
    pointer-events: none;
    ${(props) => (props.error ? 'color: #E02020;' : '')};
  }
`;

const CheckboxText = styled.div`
  a {
    text-decoration: underline;
  }
`;

const mapStateToProps = ({
  language,
  retailerSettings: { branding: { brand_call_to_action: brandCTAColor } = {} } = {},
  userPreferences: {
    preferences,
    profile,
    saveState,
    enabled: userPrefEnabled,
    isSigningUp,
    emailAddress,
    displayEmailInput,
    isSaving,
  } = {},
  returnOptions: {
    selectedOptionMethodKey,
    returnOptionMethods: { [selectedOptionMethodKey]: { id: selectedMethodId } = {} } = {},
  } = {},
}) => ({
  language,
  selectedMethodId,
  preferences,
  profile,
  brandCTAColor,
  error: saveState === EMAIL_MISMATCH,
  enabled: userPrefEnabled,
  isSigningUp,
  displayEmailInput,
  emailAddress,
  isSaving,
});

const PreferencesSignUp = ({
  dispatch,
  language,
  selectedMethodId,
  preferences,
  profile,
  brandCTAColor,
  error,
  enabled,
  isSigningUp,
  displayEmailInput,
  emailAddress,
  isSaving,
}) => {
  const hasProfile = Boolean(profile && profile.profile_id);
  const hasPreferencesAlready =
    preferences && preferences.return_methods && preferences.return_methods.length > 0;
  const hidden =
    !enabled ||
    hasProfile ||
    hasPreferencesAlready ||
    !RETURN_METHOD_PREFERENCES_LIST.includes(selectedMethodId);

  const newUser = !hasProfile || !hasPreferencesAlready;

  React.useEffect(() => {
    if (!hidden) {
      titan.event(TITAN_EVENTS.PROMOTION_VIEWED, {
        promotion_id: PROMOTION_IDS.USER_PREFERENCES,
      });
    }
  }, [hidden]);

  React.useEffect(() => {
    if (hidden || !newUser) return;
    dispatch(receiveUserPreferencesCheckboxToggle());
  }, []);

  if (hidden) return null;

  return (
    <Container>
      <Checkbox
        id="user-preference-checkbox"
        name="user-preference-checkbox"
        checked={isSigningUp}
        onChange={() => {
          if (isSaving) return;
          dispatch(receiveUserPreferencesCheckboxToggle());
        }}
        boxStyle={{
          width: '1.5rem',
          height: '1.5rem',
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: isSigningUp ? brandCTAColor : '#cccccc',
          background: brandCTAColor,
        }}
      >
        <CheckboxText
          brandCTAColor={brandCTAColor}
          dangerouslySetInnerHTML={{ __html: language['returns.step_3.preferences.description'] }}
        />
      </Checkbox>

      {displayEmailInput && (
        <EmailInputContainer error={error}>
          <div>{language['returns.step_3.preferences.email_description']}</div>
          <div
            style={{
              margin: '0.5rem auto',
              maxWidth: '18.75rem',
            }}
          >
            <TextInput
              s={{
                ...(error ? { borderColor: '#E02020' } : {}),
              }}
              className="fs-exclude"
              type="email"
              defaultValue={emailAddress}
              value={emailAddress}
              label={language['returns.step_3.preferences.email_placeholder']}
              onChange={({ target: { value = '' } } = {}) => {
                if (isSaving) return;
                dispatch(receiveUserPrefEmailAddress(value));
              }}
            />
            {error && (
              <div
                style={{
                  color: '#E02020',
                  textAlign: 'left',
                  margin: '4px auto',
                }}
              >
                {language[`returns.step_3.preferences.${EMAIL_MISMATCH}.description`]}
              </div>
            )}
          </div>
        </EmailInputContainer>
      )}
    </Container>
  );
};

PreferencesSignUp.defaultProps = {
  selectedMethodId: undefined,
  preferences: undefined,
  profile: undefined,
  brandCTAColor: undefined,
  error: false,
  emailAddress: undefined,
  displayEmailInput: false,
  isSaving: false,
};

PreferencesSignUp.propTypes = {
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.object.isRequired,
  selectedMethodId: PropTypes.string,
  preferences: PropTypes.shape({
    return_methods: PropTypes.array,
    carriers: PropTypes.array,
    locations: PropTypes.array,
  }),
  profile: PropTypes.shape({
    profile_id: PropTypes.string.isRequired,
  }),
  enabled: PropTypes.bool.isRequired,
  isSigningUp: PropTypes.bool.isRequired,
  brandCTAColor: PropTypes.string,
  error: PropTypes.bool,
  displayEmailInput: PropTypes.bool,
  emailAddress: PropTypes.string,
  retailerName: PropTypes.string.isRequired,
  isSaving: PropTypes.bool,
};

export default connect(mapStateToProps)(PreferencesSignUp);
